import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Clock, Users, Award, CheckCircle, ArrowRight, BookOpen, Calculator, HelpCircle } from 'lucide-react';
import { AIAssistantWidget } from '@/components/AIAssistantWidget';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const LifeInsuranceGuide = () => {
  const guides = [
    {
      icon: BookOpen,
      title: "Life Insurance Basics",
      description: "Understanding the fundamentals of life insurance and why you need it",
      topics: ["What is life insurance?", "Types of life insurance", "How much coverage do you need?", "Who should get life insurance?"]
    },
    {
      icon: Shield,
      title: "Choosing the Right Policy",
      description: "How to select the perfect policy for your situation and budget",
      topics: ["Term vs Whole Life", "Coverage amount calculation", "Riders and add-ons", "Comparing quotes"]
    },
    {
      icon: Calculator,
      title: "Cost & Affordability",
      description: "Understanding premiums, factors that affect cost, and budgeting tips",
      topics: ["Premium calculation", "Age and health factors", "Payment options", "Ways to save money"]
    },
    {
      icon: HelpCircle,
      title: "Common Questions",
      description: "Answers to the most frequently asked questions about life insurance",
      topics: ["Medical exam requirements", "Application process", "Claims process", "Policy changes"]
    }
  ];

  const benefits = [
    "Financial protection for your family",
    "Peace of mind knowing loved ones are covered",
    "Potential tax advantages",
    "Estate planning benefits",
    "Business protection opportunities",
    "Debt and mortgage coverage"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-primary-light/30 to-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              Life Insurance Guide
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Everything you need to know about life insurance, from the basics to advanced strategies. 
              Make informed decisions to protect your family's future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/calculator">
                <Button size="lg" variant="hero">
                  Calculate Coverage Needed
                  <Calculator className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/">
                <Button size="lg" variant="outline">
                  Get Your Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Guide Sections */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {guides.map((guide, index) => (
              <Card key={index} className="p-8 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-0">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                      <guide.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">
                      {guide.title}
                    </h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    {guide.description}
                  </p>
                  <ul className="space-y-2">
                    {guide.topics.map((topic, topicIndex) => (
                      <li key={topicIndex} className="flex items-center text-sm text-muted-foreground">
                        <CheckCircle className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Life Insurance Matters */}
      <section className="py-20 bg-primary-light/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Why Life Insurance Matters
              </h2>
              <p className="text-xl text-muted-foreground">
                Life insurance provides essential financial protection for the people who matter most to you.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-semibold text-foreground mb-6">
                  Key Benefits
                </h3>
                <div className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-background rounded-2xl p-8">
                <h3 className="text-2xl font-semibold text-foreground mb-4">
                  Did You Know?
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Users className="h-8 w-8 text-primary mr-4" />
                    <div>
                      <div className="font-semibold text-foreground">40%</div>
                      <div className="text-sm text-muted-foreground">of Americans have no life insurance</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-8 w-8 text-primary mr-4" />
                    <div>
                      <div className="font-semibold text-foreground">70%</div>
                      <div className="text-sm text-muted-foreground">underestimate the cost by 5x</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Award className="h-8 w-8 text-primary mr-4" />
                    <div>
                      <div className="font-semibold text-foreground">85%</div>
                      <div className="text-sm text-muted-foreground">say they need coverage but delay</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Use our calculator to determine how much coverage you need, then get personalized quotes in minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/calculator">
                <Button size="lg" variant="hero">
                  Calculate My Coverage
                  <Calculator className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/">
                <Button size="lg" variant="outline">
                  Get Instant Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* AI Assistant Widget */}
      <AIAssistantWidget pageContext="learn" />
    </div>
  );
};

export default LifeInsuranceGuide;